// @see https://github.com/grail-ui/grail-ui/blob/100d79b9f263caf3a974c164773f9e69704fb079/packages/grail-ui/src/resizeObserver/resizeObserver.ts
// @see https://github.com/grail-ui/grail-ui/blob/100d79b9f263caf3a974c164773f9e69704fb079/packages/grail-ui/src/resizeObserver/resizeObserver.types.ts
import type { Readable } from 'svelte/store';
import { readonly, writable } from 'svelte/store';

export type ResizeObserverConfig = {
    /**
     * Sets which box model the observer will observe changes to. Possible values
     * are `content-box` (the default), `border-box` and `device-pixel-content-box`.
     *
     * @defaultValue 'content-box'
     */
    box?: ResizeObserverBoxOptions;
};

export type ResizeObserverUseConfig = {
    /**
     * Sets which box model the observer will observe changes to. Possible values
     * are `content-box` (the default), `border-box` and `device-pixel-content-box`.
     *
     * @defaultValue 'content-box'
     */
    box?: ResizeObserverBoxOptions;
};

export type ResizeObserverReturn = {
    /**
     * Whether `ResizeObserver` is supported.
     */
    isSupported: boolean;

    /**
     * The resize data for all observed elements.
     */
    entries: Readable<Record<string, ResizeObserverEntry>>;

    /**
     * Action for the element that needs to be observed.
     */
    useResizeObserver: (
        node: HTMLElement,
        config?: ResizeObserverUseConfig,
    ) => { destroy: VoidFunction };
};

export const createResizeObserver = (
    config: ResizeObserverConfig = {},
): ResizeObserverReturn => {
    const isSupported = typeof window != 'undefined' && 'ResizeObserver' in window;

    const entries = writable<Record<string, ResizeObserverEntry>>({});

    let uid = 0;
    const useResizeObserver = (
        node: HTMLElement,
        _config: ResizeObserverUseConfig = {},
    ) => {
        let observer: ResizeObserver | undefined;

        const localId = `${uid++}`;

        if (isSupported) {
            observer = new ResizeObserver(([entry]) => {
                if (entry != null) {
                    entries.update((_entries) => ({ ..._entries, [localId]: entry }));
                }
            });
            observer.observe(node, { ...config, ..._config });
        }

        return {
            destroy() {
                observer?.disconnect();
            },
        };
    };

    return {
        isSupported,
        entries: readonly(entries),
        useResizeObserver,
    };
};
